import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarRange, Flame, ShoppingBag, RefreshCw, Save, Clock, Users, Loader2 } from 'lucide-react';
import { api } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

export default function WeekPlan() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [plan, setPlan] = useState([]);
  const [totalKcal, setTotalKcal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const dayKeys = ['day1','day2','day3','day4','day5','day6','day7'];

  const loadSaved = async () => {
    if (!user) return null;
    try {
      const { data } = await api.get('/week-plan');
      if (data?.plan?.length) { setPlan(data.plan); setTotalKcal(data.total_kcal); return data; }
    } catch (e) { console.warn('[WeekPlan] loadSaved failed', e); }
    return null;
  };

  const loadRandom = async () => {
    setLoading(true);
    try {
      const region = localStorage.getItem('region') || '';
      const { data } = await api.get('/week-plan/random', { params: region ? { region } : {} });
      setPlan(data.plan); setTotalKcal(data.total_kcal);
    } catch { toast.error('Error'); }
    finally { setLoading(false); }
  };

  // Detect user country once and cache (used to bias the plan to their region)
  useEffect(() => {
    if (localStorage.getItem('region') !== null) return;
    api.get('/geo').then(({ data }) => {
      localStorage.setItem('region', data?.region || '');
      localStorage.setItem('country', data?.country || '');
    }).catch((e) => { console.warn('[WeekPlan] /geo failed', e); localStorage.setItem('region', ''); });
  }, []);

  useEffect(() => {
    (async () => {
      const saved = await loadSaved();
      if (!saved || !saved.plan?.length) await loadRandom();
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const regenerate = async () => { setBusy(true); await loadRandom(); setBusy(false); };

  const savePlan = async () => {
    if (!user) { toast.info(t('needLogin')); return; }
    setBusy(true);
    try {
      await api.post('/week-plan', { recipe_ids: plan.map(r => r.recipe_id) });
      toast.success(t('planSaved'));
    } catch { toast.error('Error'); }
    finally { setBusy(false); }
  };

  const addAllShopping = async () => {
    if (!user) { toast.info(t('needLogin')); return; }
    setBusy(true);
    try {
      const { data } = await api.post('/week-plan/add-to-shopping', { recipe_ids: plan.map(r => r.recipe_id) });
      toast.success(`${t('planAdded')} (${data.added})`);
    } catch { toast.error('Error'); }
    finally { setBusy(false); }
  };

  if (loading) {
    return <div className="py-20 text-center text-[#5C4D44]"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="week-plan-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#C05A42] font-semibold flex items-center gap-2"><CalendarRange className="h-3.5 w-3.5" /> {t('weekPlan')}</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-display font-bold tracking-tight">{t('weekPlan')}</h1>
          {(() => { const c = typeof window !== 'undefined' ? localStorage.getItem('country') : ''; return c ? (
            <div className="mt-2 inline-flex items-center gap-1 text-xs text-[#5C4D44]" data-testid="region-badge">📍 {c}</div>
          ) : null; })()}
        </div>
        <div className="inline-flex items-center gap-2 rounded-2xl bg-white border border-black/5 px-5 py-3">
          <Flame className="h-5 w-5 text-[#E29F48]" />
          <div className="text-xs text-[#5C4D44]">{t('totalWeek')}</div>
          <div className="font-display font-bold text-xl text-[#2C2420] tabular-nums">{totalKcal}</div>
          <div className="text-xs text-[#5C4D44]">{t('kcal')}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <Button onClick={regenerate} disabled={busy} data-testid="week-regenerate" className="rounded-full bg-[#C05A42] hover:bg-[#A04832] text-white">
          {busy ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : <RefreshCw className="h-4 w-4 me-2" />}
          {t('regenerate')}
        </Button>
        <Button onClick={savePlan} disabled={busy} data-testid="week-save" className="rounded-full bg-[#7A8B76] hover:bg-[#62725f] text-white">
          <Save className="h-4 w-4 me-2" /> {t('savePlan')}
        </Button>
        <Button onClick={addAllShopping} disabled={busy} data-testid="week-add-shopping" className="rounded-full bg-[#2C2420] hover:bg-black text-white">
          <ShoppingBag className="h-4 w-4 me-2" /> {t('addPlanShopping')}
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="week-plan-grid">
        {plan.map((r, i) => {
          const title = lang === 'ar' ? r.title_ar : r.title_en;
          const kcal = r?.nutrition?.kcal;
          return (
            <Link
              key={r.recipe_id + i}
              to={`/recipe/${r.recipe_id}`}
              data-testid={`week-day-${i}`}
              className="group block fade-up"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <article className="relative overflow-hidden rounded-3xl bg-white border border-black/5 shadow-sm hover:shadow-xl transition-all hover:-translate-y-1">
                <div className="relative h-44 overflow-hidden">
                  <img src={r.image} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
                  <span className="absolute top-3 start-3 bg-white/90 text-[#2C2420] text-xs font-semibold px-3 py-1 rounded-full">
                    {t(dayKeys[i])}
                  </span>
                  {kcal ? (
                    <span className="absolute top-3 end-3 inline-flex items-center gap-1 bg-black/55 backdrop-blur-md text-white text-xs font-semibold px-3 py-1 rounded-full">
                      <Flame className="h-3 w-3" /> {kcal} {t('kcal')}
                    </span>
                  ) : null}
                  <h3 className="absolute bottom-3 start-4 end-4 text-white text-lg font-display font-semibold drop-shadow-md line-clamp-2">{title}</h3>
                </div>
                <div className="p-4 flex items-center gap-4 text-xs text-[#5C4D44]">
                  <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {r.prep_time + r.cook_time} {t('minutes')}</span>
                  <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {r.servings}</span>
                </div>
              </article>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
